import { Badge } from "@/components/Badge";
import { Flame, TrendingUp, Calendar, Shield, ArrowDown } from "lucide-react";

export const DomainWarmupHeroSection = () => {
  return (
    <section className="pt-20 sm:pt-24 pb-8 sm:pb-10">
      {/* Tag */}
      <div className="flex justify-center mb-6">
        <Badge className="bg-primary/10 text-primary border-primary/20 px-4 py-1.5">
          <Flame className="w-3.5 h-3.5 mr-1.5" />
          Domain Health
        </Badge>
      </div>

      {/* Main headline */}
      <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-center text-foreground leading-tight mb-4 sm:mb-5 max-w-4xl mx-auto">
        Email Domain Warmup Strategies{" "}
        <span className="text-primary">That Actually Work</span>
      </h1>

      {/* Subheadline */}
      <p className="text-base sm:text-lg md:text-xl text-muted-foreground text-center max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed">
        New domains face inbox placement rates as low as 40% without proper warmup. 
        Follow industry-proven schedules to build sender reputation and achieve 95%+ inbox placement.
      </p>

      {/* Key stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
        <div className="bg-card border border-border rounded-xl p-4 sm:p-5 text-center hover:border-primary/30 transition-colors">
          <div className="flex justify-center mb-2">
            <Calendar className="w-5 h-5 text-primary" />
          </div>
          <div className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-1">
            4-8 weeks
          </div>
          <div className="text-xs sm:text-sm text-muted-foreground">
            Warmup period
          </div>
          <div className="text-[10px] text-muted-foreground/60 mt-1">
            Validity 2025
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 sm:p-5 text-center hover:border-primary/30 transition-colors">
          <div className="flex justify-center mb-2">
            <TrendingUp className="w-5 h-5 text-success" />
          </div>
          <div className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-1">
            95%+
          </div>
          <div className="text-xs sm:text-sm text-muted-foreground">
            Target inbox rate
          </div>
          <div className="text-[10px] text-muted-foreground/60 mt-1">
            Industry benchmark
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 sm:p-5 text-center hover:border-primary/30 transition-colors">
          <div className="flex justify-center mb-2">
            <Flame className="w-5 h-5 text-warning" />
          </div>
          <div className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-1">
            20-50/day
          </div>
          <div className="text-xs sm:text-sm text-muted-foreground">
            Week 1 volume
          </div>
          <div className="text-[10px] text-muted-foreground/60 mt-1">
            Lemlist 2024
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 sm:p-5 text-center hover:border-primary/30 transition-colors">
          <div className="flex justify-center mb-2">
            <Shield className="w-5 h-5 text-info" />
          </div>
          <div className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-1">
            &lt;0.1%
          </div>
          <div className="text-xs sm:text-sm text-muted-foreground">
            Spam complaint goal
          </div>
          <div className="text-[10px] text-muted-foreground/60 mt-1">
            Google/Yahoo 2024
          </div>
        </div>
      </div>

      {/* ISP-specific data */}
      <div className="bg-gradient-to-r from-muted/30 via-muted/50 to-muted/30 rounded-xl p-4 sm:p-5 mb-6">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-success" />
            <span className="text-muted-foreground">Gmail: </span>
            <span className="font-semibold text-foreground">87.2% inbox rate (warmed domains)</span>
          </div>
          <div className="hidden sm:block w-px h-4 bg-border" />
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-info" />
            <span className="text-muted-foreground">Microsoft: </span>
            <span className="font-semibold text-foreground">75.6% inbox rate (warmed domains)</span>
          </div>
        </div>
        <div className="text-center text-xs text-muted-foreground/60 mt-2">
          Source: Validity Email Deliverability Benchmark 2025
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="flex justify-center">
        <div className="flex flex-col items-center gap-2 text-muted-foreground/60">
          <span className="text-xs">Scroll for warmup schedules</span>
          <ArrowDown className="w-4 h-4 animate-bounce" />
        </div>
      </div>
    </section>
  );
};
